import {
  PrimaryButton,
  SecondaryButton,
  Typography,
} from '@changegps/horizon-component-library';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { ControlledInputs } from '@/components';
import { StringRequiredSchema } from '@/utils';

import { useResetEmailTemplates, useSaveEmailTemplate } from '../../hooks';

const emailTemplateSchema = z.object({
  name: StringRequiredSchema('Please enter a name.'),
  content: z.string(),
});

type EmailTemplateForm = z.infer<typeof emailTemplateSchema>;

const EmailTemplateDefaultValues: EmailTemplateForm = {
  name: '',
  content: '',
};

type EmailTemplate = {
  name: string;
  content: string;
};

export const CreateEmailTemplate = () => {
  const { mutate: saveEmailTemplate, isPending } = useSaveEmailTemplate();
  const resetEmailTemplates = useResetEmailTemplates();

  const { control, handleSubmit, reset } = useForm<EmailTemplateForm>({
    resolver: zodResolver(emailTemplateSchema),
    defaultValues: EmailTemplateDefaultValues,
    reValidateMode: 'onBlur',
    disabled: false,
  });

  const onSubmit = (values: EmailTemplate) => {
    const updatedValues = { ...values, template_id: null };

    saveEmailTemplate(updatedValues, {
      onSuccess: () => {
        resetEmailTemplates();
      },
      onError: () => {},
      onSettled: () => {
        reset(EmailTemplateDefaultValues);
      },
    });
  };

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{ padding: '10px', display: 'flex', flexDirection: 'column', gap: 2 }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
          Name:
        </Typography>
        <ControlledInputs.TextField
          ControllerProps={{
            control,
            name: `name`,
          }}
          placeholder="Specify a name"
          fullWidth
        />
      </Box>
      <Box>
        <ControlledInputs.TextEditor
          ControllerProps={{
            control,
            name: `content`,
          }}
          modules={{
            toolbar: [
              ['bold', 'italic'],
              [{ list: 'ordered' }, { list: 'bullet' }],
            ],
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <SecondaryButton>View all merge fields</SecondaryButton>
        <PrimaryButton type="submit" disabled={isPending}>
          {isPending ? 'Creating...' : 'Create'}
        </PrimaryButton>
      </Box>
    </Box>
  );
};
